// Product Service - Uses Shopify Storefront API to fetch product data

let productsConfig = null;
let productCache = {};

// Load product configuration
async function loadProductsConfig() {
  if (productsConfig) {
    return productsConfig;
  }

  try {
    const response = await fetch('https://ervp-spa.s3.us-east-1.amazonaws.com/shade-builder/products.json');
    if (!response.ok) {
      throw new Error('Failed to load products configuration');
    }
    productsConfig = await response.json();
    return productsConfig;
  } catch (error) {
    console.error('Error loading products config:', error);
    throw error;
  }
}

// Parse dimension range from string like "0-30\"" to {min: 0, max: 30}
function parseDimensionRange(rangeStr) {
  const match = rangeStr.match(/(\d+(?:\.\d+)?)-(\d+(?:\.\d+)?)/);
  if (!match) {
    throw new Error(`Invalid range format: ${rangeStr}`);
  }
  return {
    min: parseFloat(match[1]),
    max: parseFloat(match[2])
  };
}

// GraphQL query to fetch product with variants
const PRODUCT_QUERY = `
  query getProduct($id: ID!) {
    product(id: $id) {
      id
      title
      handle
      variants(first: 250) {
        edges {
          node {
            id
            title
            price {
              amount
              currencyCode
            }
            selectedOptions {
              name
              value
            }
          }
        }
      }
    }
  }
`;

// Make Storefront API request
async function storefrontFetch(query, variables, config) {
  try {
    const response = await fetch(`https://${config.shopDomain}/api/2024-01/graphql.json`, {
      method: 'POST',
      headers: {
        'X-Shopify-Storefront-Access-Token': config.storefrontApiToken,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        query,
        variables
      })
    });

    if (!response.ok) {
      throw new Error(`Storefront API request failed: ${response.status}`);
    }

    const data = await response.json();
    
    if (data.errors) {
      console.error('GraphQL errors:', data.errors);
      throw new Error('GraphQL query failed');
    }

    return data.data;
  } catch (error) {
    console.error('Storefront API error:', error);
    throw error;
  }
}

// Load product data from Shopify Storefront API
async function loadShopifyProduct(productGid, config) {
  // Check cache first
  if (productCache[productGid]) {
    return productCache[productGid];
  }

  try {
    console.log(`Fetching product with GID: ${productGid}`);
    
    const data = await storefrontFetch(PRODUCT_QUERY, { id: productGid }, config);
    
    if (!data.product) {
      throw new Error(`Product not found: ${productGid}`);
    }
    
    const product = data.product;
    console.log(`Loaded product data:`, product);
    
    // Process variants to add parsed ranges
    const processedVariants = product.variants.edges.map(({ node: variant }) => {
      // Find width and height from selectedOptions
      const widthOption = variant.selectedOptions.find(opt => opt.name === 'Width');
      const heightOption = variant.selectedOptions.find(opt => opt.name === 'Height');
      
      if (!widthOption || !heightOption) {
        console.warn(`Variant ${variant.id} missing width or height option`);
        return null;
      }
      
      const widthRange = parseDimensionRange(widthOption.value);
      const heightRange = parseDimensionRange(heightOption.value);
      
      return {
        id: variant.id,
        title: variant.title,
        price: parseFloat(variant.price.amount),
        widthRange,
        heightRange,
        option1: widthOption.value,
        option2: heightOption.value
      };
    }).filter(Boolean); // Remove any null variants
    
    const processedProduct = {
      id: product.id,
      title: product.title,
      handle: product.handle,
      variants: processedVariants
    };
    
    // Cache the processed product
    productCache[productGid] = processedProduct;
    
    return processedProduct;
  } catch (error) {
    console.error(`Error loading product ${productGid}:`, error);
    throw error;
  }
}

// Resolve tile "From" prices from the same live Shopify variant data used by
// the final quote. Marketing prices must never be hard-coded here.
export async function getStartingPricesByShadeType() {
  const config = await loadProductsConfig();
  const shadeTypes = ['pleated', 'valu-line', 'premium'];
  const configurations = ['night', 'day-night'];

  const entries = await Promise.all(shadeTypes.map(async (shadeType) => {
    const products = await Promise.all(configurations.map((shadeConfiguration) => {
      const key = getProductKey(shadeType, shadeConfiguration);
      const productConfig = config.products[key];
      if (!productConfig) throw new Error(`Product configuration not found: ${key}`);
      return loadShopifyProduct(productConfig.gid, config);
    }));
    const prices = products.flatMap(product => product.variants.map(variant => variant.price));
    if (!prices.length) throw new Error(`No live variants found for ${shadeType}`);
    return [shadeType, Math.min(...prices)];
  }));

  return Object.fromEntries(entries);
}

// Get product key based on shade type and configuration
export function getProductKey(shadeType, shadeConfiguration) {
  const typeMap = {
    'pleated': 'pleated',
    'valu-line': 'valu-line',
    'premium': 'premium'
  };
  
  const configMap = {
    'night': 'night',
    'day-night': 'day-night'
  };
  
  return `${typeMap[shadeType]}-${configMap[shadeConfiguration]}`;
}

// Find the appropriate variant based on dimensions
export function findVariantForDimensions(product, width, height) {
  if (!product || !product.variants) {
    console.error('No product or variants available');
    return null;
  }

  console.log(`Finding variant for dimensions: ${width}" x ${height}"`);
  console.log('Available variants:', product.variants.map(v => ({
    title: v.title,
    widthRange: v.widthRange,
    heightRange: v.heightRange
  })));

  // Shopify's option labels use whole-inch bands (for example 0-30 and
  // 31-48), while the builder accepts quarter inches. A measurement of
  // 30.25 must therefore move up to the band capped at 48, not fall through
  // to an arbitrary "closest" variant. Select the smallest upper bounds that
  // can contain both measurements; this also makes the result independent of
  // Shopify's variant ordering.
  const candidates = product.variants
    .filter(v => width <= v.widthRange.max && height <= v.heightRange.max)
    .sort((a, b) =>
      a.widthRange.max - b.widthRange.max ||
      a.heightRange.max - b.heightRange.max
    );
  const variant = candidates[0] || null;

  if (variant) {
    console.log(`Match found: ${variant.title}`);
  }

  if (!variant) {
    console.error(`No variant found for ${width}" x ${height}"`);
  }

  return variant;
}

// Get complete product and variant info for a configuration
export async function getProductVariant(shadeType, shadeConfiguration, width, height) {
  const config = await loadProductsConfig();
  const productKey = getProductKey(shadeType, shadeConfiguration);
  const productConfig = config.products[productKey];
  
  if (!productConfig) {
    throw new Error(`Product configuration not found for ${shadeType} ${shadeConfiguration}`);
  }

  // Load product from Shopify Storefront API
  const product = await loadShopifyProduct(productConfig.gid, config);
  
  const variant = findVariantForDimensions(product, width, height);
  
  if (!variant) {
    throw new Error(`No variant found for dimensions ${width}"x${height}"`);
  }

  return {
    product,
    variant
  };
}

// Calculate total price including rush order
export function calculateTotalPrice(variant, rushOrder, config) {
  const basePrice = variant.price;
  const rushFee = rushOrder ? (config.rushOrderProduct?.price || 50) : 0;
  return (basePrice + rushFee).toFixed(2);
}

// Get color name from hex code
function getColorName(component, colorValue) {
  // Since we're now using color names directly, just return the value
  return colorValue || 'Not selected';
}

// Calculate delivery window dates
function calculateDeliveryWindow(rushOrder) {
  const today = new Date();
  const startDays = rushOrder ? 5 : 12;
  const endDays = rushOrder ? 8 : 15;
  
  const startDate = new Date(today);
  startDate.setDate(today.getDate() + startDays);
  
  const endDate = new Date(today);
  endDate.setDate(today.getDate() + endDays);
  
  // Format dates as "Month Day"
  const formatDate = (date) => {
    const months = ['January', 'February', 'March', 'April', 'May', 'June', 
                   'July', 'August', 'September', 'October', 'November', 'December'];
    return `${months[date.getMonth()]} ${date.getDate()}`;
  };
  
  return `${formatDate(startDate)} – ${formatDate(endDate)}`;
}

// Format line item properties for Shopify
export function formatLineItemProperties(state, exactWidth, exactHeight) {
  const properties = {
    'Exact Width': `${exactWidth}"`,
    'Exact Height': `${exactHeight}"`,
    'Top Rail Color': getColorName('topRail', state.colors.topRail),
    'Night Shade Color': getColorName('nightShade', state.colors.nightShade),
  };

  if (state.shadeConfiguration === 'day-night') {
    properties['Day Shade Color'] = getColorName('dayShade', state.colors.dayShade);
  }

  if (state.rushOrder) {
    properties['Rush Order'] = 'Yes (+$50)';
  }

  // Add delivery window as a line item property so it's visible to customers
  properties['Delivery Window'] = calculateDeliveryWindow(state.rushOrder);

  return properties;
}

// Extract numeric variant ID from GID
function extractVariantId(gid) {
  // Extract numeric ID from "gid://shopify/ProductVariant/123456789"
  const match = gid.match(/\/(\d+)$/);
  return match ? match[1] : null;
}

const DEFAULT_CART_RETRY_DELAYS_MS = [350, 800];

function sleep(ms) {
  return new Promise(resolve => setTimeout(resolve, ms));
}

function retryDelayMs(response, fallbackMs) {
  const retryAfter = response.headers?.get?.('Retry-After');
  if (!retryAfter) return fallbackMs;

  const seconds = Number(retryAfter);
  if (Number.isFinite(seconds)) return Math.max(0, seconds * 1000);

  const retryAt = Date.parse(retryAfter);
  return Number.isNaN(retryAt) ? fallbackMs : Math.max(0, retryAt - Date.now());
}

// Shopify may briefly throttle cart mutations. Retry only explicit 429 responses;
// other failures surface immediately so customer errors are not hidden.
export async function fetchWith429Retry(url, options, {
  retryDelaysMs = DEFAULT_CART_RETRY_DELAYS_MS,
  fetchImpl = fetch,
  sleepImpl = sleep
} = {}) {
  let response = await fetchImpl(url, options);

  for (const fallbackDelayMs of retryDelaysMs) {
    if (response.status !== 429) return response;
    await sleepImpl(retryDelayMs(response, fallbackDelayMs));
    response = await fetchImpl(url, options);
  }

  return response;
}

// Add to Shopify cart
export async function addToShopifyCart(state, quantity = 1) {
  try {
    const config = await loadProductsConfig();
    
    // Get product and variant
    const { product, variant } = await getProductVariant(
      state.selectedType,
      state.shadeConfiguration,
      state.dimensions.width,
      state.dimensions.height
    );

    // Extract numeric variant ID
    const variantId = extractVariantId(variant.id);
    if (!variantId) {
      throw new Error('Invalid variant ID format');
    }

    // Check if rush promo applies (3+ shades)
    const isRushPromoActive = quantity >= 3 && state.rushOrder;

    // Format properties
    const properties = formatLineItemProperties(
      state,
      state.dimensions.width,
      state.dimensions.height
    );

    // Add promo info to properties if applicable
    if (isRushPromoActive) {
      properties['Rush Order'] = 'Yes (FREE with 3+ Shades)';
      properties['Promotion Applied'] = '3+ Shades = Free Rush Order';
    }

    // Build items array
    const items = [{
      id: variantId,
      quantity: quantity,
      properties
    }];

    // Add rush order product when selected (Shopify will apply discount for 3+ shades)
    if (state.rushOrder && config.rushOrderProduct?.variantId) {
      const rushProperties = {
        'Related Product': `${product.title}`,
        'Shade Configuration': `${state.selectedType} - ${state.shadeConfiguration === 'day-night' ? 'Day & Night' : 'Night Only'}`,
        'Dimensions': `${state.dimensions.width}" W × ${state.dimensions.height}" H`,
        'Shade Quantity': `${quantity}`,
        'Delivery Window': calculateDeliveryWindow(state.rushOrder)
      };

      // Add promo tracking to rush order product
      if (isRushPromoActive) {
        rushProperties['Promotion'] = '3+ Shades Free Rush Order';
        rushProperties['Discount'] = 'Automatic 100% discount applied';
      }

      items.push({
        id: config.rushOrderProduct.variantId,
        quantity: 1,
        properties: rushProperties
      });
    }

    // Add free bracket kit (1 per shade)
    if (config.bracketKits && config.bracketKits[state.selectedType]) {
      items.push({
        id: config.bracketKits[state.selectedType],
        quantity: quantity,
        properties: {
          'Free with Shade Purchase': 'Yes',
          'Related Shade': `${product.title}`,
          'Shade Type': `${state.selectedType}`,
          'Shade Configuration': `${state.shadeConfiguration === 'day-night' ? 'Day & Night' : 'Night Only'}`,
          'Dimensions': `${state.dimensions.width}" W × ${state.dimensions.height}" H`
        }
      });
    }

    // Add to cart using JSON API for multiple items
    const response = await fetchWith429Retry('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ items })
    });

    if (!response.ok) {
      throw new Error(`Failed to add to cart: ${response.status}`);
    }

    return response.json();
  } catch (error) {
    console.error('Error adding to cart:', error);
    throw error;
  }
}


// Get price for display
export async function getPrice(state) {
  try {
    const config = await loadProductsConfig();
    console.log('Getting price for:', {
      type: state.selectedType,
      config: state.shadeConfiguration,
      width: state.dimensions.width,
      height: state.dimensions.height
    });
    
    const { variant } = await getProductVariant(
      state.selectedType,
      state.shadeConfiguration,
      state.dimensions.width,
      state.dimensions.height
    );
    
    console.log('Found variant:', variant);
    
    return calculateTotalPrice(variant, state.rushOrder, config);
  } catch (error) {
    console.error('Error calculating price:', error);
    console.error('State was:', state);
    return null;
  }
}

// Preload all products (optional - for better performance)
export async function preloadAllProducts() {
  try {
    const config = await loadProductsConfig();
    const gids = Object.values(config.products).map(p => p.gid);
    
    // Load all products in parallel
    await Promise.all(gids.map(gid => loadShopifyProduct(gid, config)));
    
    console.log('All products preloaded successfully');
  } catch (error) {
    console.error('Error preloading products:', error);
  }
}